import {
  Action,
  AionzDialog,
  AionzDialogRef,
  AionzSnackbar,
  ConfirmResult,
  ConfirmationModalService,
  DialogSize,
  LoadingService,
  ModalTypes,
  SeedButtonModule, AionzTooltip } from '@aionzdev/design-system';
import { DatePipe, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import {
  auditTime,
  catchError,
  combineLatest,
  concatMap,
  distinctUntilChanged,
  finalize,
  forkJoin,
  from,
  map,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError
} from 'rxjs';
import { AbstractUserService, WebsocketService } from '@aionzdev/modules';
import { LoadState, PurchaseRequestDetailTab, PurchaseRequestStatus } from '../../../enums';
import { PurchaseItemStatus } from '../../../interfaces/purchase-request';
import { PurchaseRequestItemTableAction, PurchaseRequestItemTableActionEvent } from '../../../interfaces/purchase-request-item-table-action';
import { PurchasingEvent } from '../../../interfaces/purchasing-event';
import { UpdatePurchaseOrderItemDto } from '../../../interfaces/update-purchase-order-status.dto';
import { DuplicatedProductResponseDto } from '../../../interfaces/duplicated-product-response.dto';
import { MergeItem } from '../../../interfaces/merge-purchase-requests-dialog-data';
import { PurchaseRequestResponseDto } from '../../../interfaces/purchase-response.dto';
import { PurchaseSupplier, PurchaseSupplierCreate, PurchaseSupplierLinkStatus } from '../../../interfaces/purchase-supplier';
import { SendPurchaseRequestForQuoteItem } from '../../../interfaces/send-purchase-request-for-quote-dialog-data';
import { AbstractPurchasingService } from '../../../services/abstract-purchasing.service';
import { PurchaseRequestStatusComponent } from '../../atoms/purchase-request-status/purchase-request-status.component';
import { MergePurchaseRequestsDialogComponent } from '../../components/merge-purchase-requests-dialog/merge-purchase-requests-dialog.component';
import { BranchesTooltipComponent } from '../../atoms/branches-tooltip/branches-tooltip.component';
import {
  branchLabel,
  deliveryBranchesLabel,
  injectBranchCities,
  toDeliveryBranchesSummary
} from '../../utils/branch-label.util';
import { PurchaseSuppliersListDialogComponent } from '../../components/purchase-suppliers-list-dialog/purchase-suppliers-list-dialog.component';
import {
  NewSupplierDialogComponent,
  NewSupplierDialogData,
  NewSupplierDialogItem,
  NewSupplierSaveEvent
} from '../../components/new-supplier-dialog/new-supplier-dialog.component';
import { PurchaseRequestAttachmentsComponent } from '../../components/purchase-request-attachments/purchase-request-attachments.component';
import { PurchaseRequestActivityPanelComponent } from '../../components/purchase-request-activity-panel/purchase-request-activity-panel.component';
import { PurchaseRequestItemsTableComponent } from '../../components/purchase-request-items-table/purchase-request-items-table.component';

/** Tipo de evento realtime da busca de fornecedores sugeridos (Cotação Inteligente). */
const SUPPLIER_SUGGESTION_EVENT_TYPE = 'fornecedor_sugerido';

/** "Fornecedor adicionado a 3 itens (2 já tinham este fornecedor)." */
function bulkSupplierMessage(added: number, alreadyThere: number): string {
  const itens = (n: number) => `${n} ${n === 1 ? 'item' : 'itens'}`;
  if (added === 0) return `Este fornecedor já estava ${alreadyThere === 1 ? 'no item escolhido' : `nos ${itens(alreadyThere)} escolhidos`}.`;
  const base = `Fornecedor adicionado a ${itens(added)}.`;
  return alreadyThere > 0
    ? `${base.slice(0, -1)} (${alreadyThere} já ${alreadyThere === 1 ? 'tinha' : 'tinham'} este fornecedor).`
    : base;
}

@Component({
  selector: 'aionz-accepted-purchase-request-detail',
  imports: [
    DatePipe,
    AionzTooltip,
    MatButtonModule,
    MatChipsModule,
    MatIconModule,
    MatTabsModule,
    PurchaseRequestStatusComponent,
    PurchaseRequestAttachmentsComponent,
    PurchaseRequestActivityPanelComponent,
    PurchaseRequestItemsTableComponent,
    SeedButtonModule
  ],
  templateUrl: './accepted-purchase-request-detail.component.html',
  styleUrl: './accepted-purchase-request-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AcceptedPurchaseRequestDetailComponent implements OnInit {
  private readonly dialog = inject(AionzDialog);
  private readonly confirmationModal = inject(ConfirmationModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly loadingService = inject(LoadingService);
  private readonly purchasingService = inject(AbstractPurchasingService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(AionzSnackbar);
  private readonly userService = inject(AbstractUserService);
  private readonly returnUrl = this.router.currentNavigation()?.extras.state?.['returnUrl'] as
    | string
    | undefined;

  protected readonly activityPanel = viewChild.required(PurchaseRequestActivityPanelComponent);

  protected readonly purchase = signal<PurchaseRequestResponseDto | null>(null);
  protected readonly lastSupplierSuggestionSearchAt = computed(
    () => this.purchase()?.lastSupplierSuggestionSearchAt ?? null
  );
  protected readonly items = signal<SendPurchaseRequestForQuoteItem[]>([]);
  protected readonly duplicatedItems = signal<DuplicatedProductResponseDto[]>([]);

  protected readonly branchesTooltipComponent = BranchesTooltipComponent;

  protected readonly branchCities = injectBranchCities();
  /** Locais de entrega dos itens, para o chip do topo (nome e endereço na dica). */
  protected readonly deliveryBranchesSummary = computed(() =>
    toDeliveryBranchesSummary(this.items(), this.branchCities())
  );
  protected readonly deliveryBranchesLabel = computed(() =>
    deliveryBranchesLabel(this.deliveryBranchesSummary())
  );
  protected readonly selectedTabIndex = signal(PurchaseRequestDetailTab.DETAILS);
  protected readonly detailsLoadState = signal<LoadState>(LoadState.IDLE);
  protected readonly LoadState = LoadState;
  protected readonly PurchaseRequestDetailTab = PurchaseRequestDetailTab;
  protected readonly PurchaseRequestStatus = PurchaseRequestStatus;
  protected readonly detailsInitialized = signal(false);
  protected readonly expectedDeliveryDateLabel = computed(() => {
    const purchase = this.purchase();
    if (!purchase || typeof purchase.remainingDays !== 'number') return null;

    const deliveryDate = new Date();
    deliveryDate.setHours(0, 0, 0, 0);
    deliveryDate.setDate(deliveryDate.getDate() + purchase.remainingDays);
    return deliveryDate;
  });

  protected readonly itemsWithoutSuppliersCount = computed(
    () => this.items().filter(item => !item.suppliers?.length).length
  );

  protected readonly hasItemsWithoutSuppliers = computed(() => this.itemsWithoutSuppliersCount() > 0);

  /** Por que "Enviar para cotação" está desabilitado (null quando está liberado). */
  protected readonly sendForQuoteBlockedReason = computed(() => {
    const n = this.itemsWithoutSuppliersCount();
    if (n === 0) return null;
    const itens = n === 1 ? '1 item está sem fornecedor' : `${n} itens estão sem fornecedor`;
    return `${itens}. Adicione ao menos um fornecedor em cada item para enviar para cotação.`;
  });

  /**
   * Quantidade de respostas de busca de fornecedores sugeridos ainda
   * pendentes (uma por produto enfileirado). `null` = nenhuma busca em
   * andamento. Usado só para controlar o loading do botão nesta sessão —
   * não persiste em F5/saída de página.
   */
  private readonly pendingSupplierSuggestionResponses = signal<number | null>(null);
  protected readonly isSearchingSuppliers = computed(
    () => this.pendingSupplierSuggestionResponses() !== null
  );

  private readonly websocketService = inject(WebsocketService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly websocketUpdate$ = new Subject<PurchasingEvent>();
  private readonly detailRealtimeDestroy$ = new Subject<void>();
  protected readonly tableActions = signal<PurchaseRequestItemTableAction[]>([
    {
      type: 'remove-item',
      label: 'Remover item',
      icon: 'delete'
    }
  ]);

  protected currentPurchaseId = '';
  private suppliersDialogRef?: AionzDialogRef<PurchaseSuppliersListDialogComponent>;
  private mergeDialogRef?: AionzDialogRef<MergePurchaseRequestsDialogComponent>;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (isPlatformBrowser(this.platformId)) {
        const config = this.purchasingService.getRealtimeConfig();
        this.websocketService.sendMessage(config.leaveEvent, config.tenantId);
      }
      this.detailRealtimeDestroy$.next();
      this.detailRealtimeDestroy$.complete();
      this.mergeDialogRef?.close();
      this.suppliersDialogRef?.close();
    });
  }

  ngOnInit(): void {
    combineLatest([
      this.route.paramMap.pipe(
        map(params => params.get('purchaseId') || ''),
        distinctUntilChanged()
      ),
      this.route.queryParamMap.pipe(
        map(params => params.get('tab')),
        distinctUntilChanged()
      )
    ])
      .pipe(
        tap(([purchaseId, tab]) => {
          const tabIndex =
            tab === 'anexos'
              ? PurchaseRequestDetailTab.ATTACHMENTS
              : PurchaseRequestDetailTab.DETAILS;
          const isNewPurchase = purchaseId !== this.currentPurchaseId;

          if (isNewPurchase) {
            this.currentPurchaseId = purchaseId;
            this.loadDetail(purchaseId);

            if (isPlatformBrowser(this.platformId)) {
              this.setupRealtimeUpdates();
            }
          }

          this.selectedTabIndex.set(tabIndex);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Rebusca a solicitação direto do backend (não do resolver, que fica
   * congelado na navegação) — usado pelas atualizações via WebSocket, para
   * refletir campos que mudam em background (ex.: fornecedor sugerido,
   * última busca) sem exigir F5.
   */
  private setupRealtimeUpdates(): void {
    this.detailRealtimeDestroy$.next();

    if (!this.currentPurchaseId) return;

    const config = this.purchasingService.getRealtimeConfig();
    const updateTypes = new Set(config.updateTypes.purchaseDetail);

    this.websocketUpdate$
      .pipe(auditTime(500), takeUntil(this.detailRealtimeDestroy$), takeUntilDestroyed(this.destroyRef))
      .subscribe(event => this.refreshPurchaseData(event));

    this.websocketService.sendMessage(config.joinEvent, config.tenantId);

    this.websocketService.listen(config.eventName, (event: unknown) => {
      const mappedEvent: PurchasingEvent = this.purchasingService.getPurchasingEvent(event);
      if (
        String(mappedEvent.tenantId) === String(config.tenantId) &&
        mappedEvent.purchaseId === this.currentPurchaseId &&
        updateTypes.has(mappedEvent.type)
      ) {
        this.websocketUpdate$.next(mappedEvent);
      }
    });
  }

  protected handleGoBack(): void {
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
      return;
    }

    this.router.navigate(['../../quadro'], { relativeTo: this.route });
  }

  protected handleTabChange(event: MatTabChangeEvent): void {
    const tab = event.index === PurchaseRequestDetailTab.ATTACHMENTS ? 'anexos' : 'detalhes';
    this.selectedTabIndex.set(event.index);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  protected openMergeDialog(): void {
    const purchase = this.purchase();
    const duplicatedItems = this.duplicatedItems();
    if (!purchase || !duplicatedItems.length) return;

    const mappedItems = duplicatedItems.map(duplicatedItem => ({
      orderId: duplicatedItem.orderId,
      productId: duplicatedItem.productId,
      name: duplicatedItem.product.name,
      specification: duplicatedItem.product.specification,
      costCenter: duplicatedItem.product.costCenter,
      requester: duplicatedItem.requester,
      unit: duplicatedItem.product.unit,
      quantity: duplicatedItem.quantity,
      suppliers: duplicatedItem.suppliers,
      purchaseId: duplicatedItem.purchaseId
    }));

    this.mergeDialogRef = this.dialog.open(MergePurchaseRequestsDialogComponent, {
      data: {
        id: purchase.id,
        title: purchase.title,
        description: purchase.description,
        requestedAt: purchase.requestedAt,
        status: purchase.status[0],
        items: mappedItems,
        currentItems: this.items()
      },
      size: DialogSize.LG
    });

    const dialogRef = this.mergeDialogRef;
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result?.action === 'unify') {
          this.handleMergeRequests(result.requests);
        }
        this.mergeDialogRef = undefined;
      });

  }

  private bulkSupplierDialogOpen = false;

  /**
   * Adicionar fornecedor em lote: o mesmo dialog do mapa, com "Todos os itens / Escolher itens".
   * Aqui os fornecedores são montados por produto, então mesmo "todos" vira uma chamada por
   * produto (como adicionar item a item); o convite só sai quando a solicitação vai para cotação.
   */
  protected openBulkSupplierDialog(): void {
    if (this.bulkSupplierDialogOpen) return;
    this.bulkSupplierDialogOpen = true;

    // Um produto pode aparecer em mais de uma linha (um por local de entrega); o vínculo com o
    // fornecedor é por produto, então a lista junta essas linhas e mostra os locais juntos.
    const byProduct = new Map<string, NewSupplierDialogItem>();
    for (const item of this.items()) {
      const branch = item.branchName || item.branch;
      const existing = byProduct.get(item.productId);
      const supplierCnpjs = item.suppliers.map(supplier => (supplier.cnpj ?? '').replace(/[^0-9A-Z]/gi, '').toUpperCase()).filter(Boolean);
      if (existing) {
        if (branch && !existing.branch?.split(', ').includes(branch)) {
          existing.branch = existing.branch ? `${existing.branch}, ${branch}` : branch;
        }
        existing.currentSupplierCnpjs = [...new Set([...(existing.currentSupplierCnpjs ?? []), ...supplierCnpjs])];
        continue;
      }
      byProduct.set(item.productId, {
        productId: item.productId,
        code: item.ERPId != null ? String(item.ERPId) : '',
        product: item.name,
        branch,
        currentSupplierCnpjs: supplierCnpjs
      });
    }
    const items = [...byProduct.values()];

    const dialogRef = this.dialog.open<NewSupplierDialogComponent, NewSupplierDialogData, PurchaseSupplier>(
      NewSupplierDialogComponent,
      { autoFocus: true, data: { items } }
    );
    dialogRef.componentInstance.savesBeforeClosing = true;

    const searchSubscription = dialogRef.componentInstance.searchCNPJ.subscribe(({ cnpj, callback }) =>
      this.userService.findByCNPJ(cnpj).subscribe({
        next: user => callback(user ?? null),
        error: () => callback(null)
      })
    );
    const saveSubscription = dialogRef.componentInstance.save.subscribe(event =>
      this.createSupplierInBulk(event, items.map(item => item.productId))
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.bulkSupplierDialogOpen = false;
        searchSubscription.unsubscribe();
        saveSubscription.unsubscribe();
      });
  }

  /** Uma chamada por produto, em sequência; 409 = já estava no item (conta como feito). */
  private createSupplierInBulk(
    { supplier, productIds, progress, done }: NewSupplierSaveEvent,
    allProductIds: string[]
  ): void {
    const targets = productIds ?? allProductIds;
    const total = targets.length;
    let added = 0;
    let alreadyThere = 0;
    progress(0, total);

    from(targets)
      .pipe(
        concatMap(productId =>
          this.purchasingService
            .createSupplierForProduct(
              { ...supplier, purchaseId: this.currentPurchaseId, productId },
              { hideGlobalError: true }
            )
            .pipe(
              map(response => ({ productId, created: response.body as PurchaseSupplier | null })),
              catchError((error: HttpErrorResponse) =>
                error.status === 409 ? of({ productId, created: null }) : throwError(() => error)
              )
            )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ productId, created }) => {
          if (created) this.pushNewCreateSupplier(created, productId);
          else alreadyThere++;
          progress(++added, total);
        },
        complete: () => {
          this.activityPanel().reloadActivities();
          this.snackBar.success(bulkSupplierMessage(total - alreadyThere, alreadyThere));
          done(true);
        },
        error: () => {
          if (added > 0) this.activityPanel().reloadActivities();
          this.snackBar.error(
            added > 0
              ? `Adicionado em ${added} de ${total} itens. Tente de novo para completar.`
              : 'Não foi possível adicionar o fornecedor. Tente novamente.'
          );
          done(false);
        }
      });
  }

  protected openSuppliersDialog(itemForQuote: SendPurchaseRequestForQuoteItem): void {
    this.suppliersDialogRef = this.dialog.open<PurchaseSuppliersListDialogComponent>(
      PurchaseSuppliersListDialogComponent,
      {
        size: DialogSize.FULL,
        data: itemForQuote.suppliers
      }
    );

    const dialogRef = this.suppliersDialogRef;
    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.suppliersDialogRef = undefined;
      });

    const createSupplierSubscription = dialogRef.componentInstance.createSupplier.subscribe(
      (newSupplier: PurchaseSupplier) =>
        this.handleCreateSupplier({
          ...newSupplier,
          purchaseId: this.currentPurchaseId,
          productId: itemForQuote.productId
        })
    );

    const deleteSupplierSubscription = dialogRef.componentInstance.deleteSupplier.subscribe(
      (deletedSupplier: PurchaseSupplier) =>
        this.handleDeleteSupplier({
          purchaseId: this.currentPurchaseId,
          supplierId: deletedSupplier.id,
          productId: itemForQuote.productId
        })
    );

    const approveSupplierSubscription = dialogRef.componentInstance.approveSuggestedSupplier.subscribe(
      (supplier: PurchaseSupplier) =>
        this.handleUpdateSuggestedSupplierStatus(supplier, itemForQuote.productId)
    );

    this.destroyRef.onDestroy(() => {
      createSupplierSubscription.unsubscribe();
      deleteSupplierSubscription.unsubscribe();
      approveSupplierSubscription.unsubscribe();
    });
  }

  protected sendForQuote(): void {
    const hasPendingSuggestedSupplier = this.items().some(item =>
      item.suppliers.some(supplier => supplier.linkStatus === PurchaseSupplierLinkStatus.PENDENTE)
    );

    this.confirmationModal
      .open(
        {
          headerTitle: 'Enviar para Cotação',
          title: 'Deseja enviar essa solicitação para cotação?',
          description: hasPendingSuggestedSupplier
            ? 'Após o envio, os fornecedores serão notificados e poderão enviar suas cotações. Há fornecedores sugeridos ainda pendentes de avaliação — eles não participarão desta cotação.'
            : 'Após o envio, os fornecedores serão notificados e poderão enviar suas cotações.',
          icon: {
            name: 'send',
            color: 'var(--mat-sys-primary)'
          },
          labelConfirm: 'Enviar para cotação',
          labelCancel: 'Ainda não'
        },
        ModalTypes.INFO
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result === Action.CONFIRM) {
          this.startQuoting();
        }
      });
  }

  protected handleTriggerSupplierSuggestionSearch(): void {
    if (this.isSearchingSuppliers()) return;

    // Loading do botão liga já no clique (feedback imediato) e só desliga
    // quando as N respostas dos produtos enfileirados chegarem via
    // WebSocket — ou aqui mesmo, se a chamada falhar ou não enfileirar nada.
    this.pendingSupplierSuggestionResponses.set(0);

    this.purchasingService
      .triggerSupplierSuggestionSearch(this.currentPurchaseId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          const result = response.body;
          const emCooldown = result?.produtosEmCooldown?.length ?? 0;
          const enfileirados = result?.produtosEnfileirados?.length ?? 0;

          if (enfileirados > 0) {
            this.pendingSupplierSuggestionResponses.set(enfileirados);
            this.snackBar.success('Nova busca de fornecedores sugeridos iniciada com sucesso!', undefined, {
              duration: 3000
            });
          } else {
            this.pendingSupplierSuggestionResponses.set(null);
          }

          if (emCooldown > 0) {
            this.snackBar.info(
              enfileirados > 0
                ? `${emCooldown} ${emCooldown === 1 ? 'produto está' : 'produtos estão'} em período de espera e não ${emCooldown === 1 ? 'foi incluído' : 'foram incluídos'} nesta busca.`
                : 'Todos os produtos estão em período de espera. Tente novamente mais tarde.'
            );
          }

          if (enfileirados === 0 && emCooldown === 0) {
            this.snackBar.info('Nenhum produto disponível para busca de fornecedores sugeridos no momento.');
          }

          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.pendingSupplierSuggestionResponses.set(null);
          this.snackBar.error('Erro ao disparar a busca de fornecedores sugeridos.');
        }
      });
  }

  protected handleDeclineRequest(): void {
    this.confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Recusar solicitação',
          title: 'Motivo da recusa',
          description: '',
          labelConfirm: 'Desejo recusar e enviar motivo da recusa'
        },
        {
          justificationPlaceholder: 'Digite aqui o motivo pelo qual o pedido foi recusado.',
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmResult) => {
        if (result.action !== Action.CONFIRM || !result.justification) {
          return;
        }

        this.loadingService.showLoading();
        this.purchasingService
          .cancelPurchaseRequest(this.currentPurchaseId, result.justification)
          .pipe(
            finalize(() => this.loadingService.hideLoading()),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: () => {
              this.snackBar.success('Solicitação cancelada com sucesso!');
              this.handleGoBack();
            },
            error: () => {
              this.snackBar.error('Erro ao cancelar a solicitação.');
            }
          });
      });
  }

  private loadDetail(purchaseId: string): void {
    const resolvedPurchase = this.route.snapshot.data[
      'purchase'
    ] as PurchaseRequestResponseDto | null;

    if (!purchaseId || !resolvedPurchase) {
      this.snackBar.error('Solicitação não encontrada.');
      this.handleGoBack();
      return;
    }

    this.detailsLoadState.set(LoadState.LOADING);
    forkJoin({
      items: this.purchasingService.findAllProductsForQuote(
        purchaseId,
        PurchaseRequestStatus.ACCEPTED
      ),
      duplicatedItems: this.purchasingService.checkForDuplicatedProducts(purchaseId)
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ items, duplicatedItems }) => {
          this.purchase.set({ ...resolvedPurchase, status: [PurchaseRequestStatus.ACCEPTED] });
          this.items.set(items.body || []);
          this.duplicatedItems.set(duplicatedItems.body || []);
          this.detailsLoadState.set(LoadState.LOADED);
        },
        error: () => {
          this.detailsLoadState.set(LoadState.ERROR);
          this.showErrorDialog(() => this.loadDetail(purchaseId));
        }
      });
  }

  private refreshPurchaseData(event: PurchasingEvent): void {
    forkJoin({
      purchase: this.purchasingService.findPurchaseById(this.currentPurchaseId),
      items: this.purchasingService.findAllProductsForQuote(
        this.currentPurchaseId,
        PurchaseRequestStatus.ACCEPTED
      )
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ purchase, items }) => {
          if (purchase.body) {
            this.purchase.set({ ...purchase.body, status: [PurchaseRequestStatus.ACCEPTED] });
          }
          if (items.body) {
            this.items.set(items.body);
          }

          if (event.type === SUPPLIER_SUGGESTION_EVENT_TYPE) {
            this.registerSupplierSuggestionResponse();

            if (event.encontrouFornecedor === false) {
              this.snackBar.info('Nenhum fornecedor novo foi encontrado nesta busca.', undefined, {
                duration: 4000
              });
            } else if (event.encontrouFornecedor === true) {
              this.snackBar.success('Novo(s) fornecedor(es) sugerido(s) encontrado(s)!', undefined, {
                duration: 4000
              });
            }
          }
        }
      });
  }

  /**
   * Cada evento `fornecedor_sugerido` recebido corresponde à resposta de UM
   * produto enfileirado na busca (a fila do backend processa item a item).
   * O evento não identifica qual produto respondeu, então só dá para contar
   * quantas respostas já chegaram, não riscar produtos específicos — chega
   * a 0 pendentes quando todas as N respostas dos produtos enfileirados
   * tiverem chegado, desligando o loading do botão.
   */
  private registerSupplierSuggestionResponse(): void {
    const pending = this.pendingSupplierSuggestionResponses();
    if (pending === null) return;

    const remaining = pending - 1;
    this.pendingSupplierSuggestionResponses.set(remaining > 0 ? remaining : null);
  }

  private handleCreateSupplier(supplier: PurchaseSupplierCreate): void {
    this.loadingService.showLoading();
    this.purchasingService
      .createSupplierForProduct(supplier)
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: response => {
          if (response.body && supplier.productId) {
            this.pushNewCreateSupplier(response.body, supplier.productId);
          }
          this.snackBar.success('Fornecedor criado com sucesso!');
          this.activityPanel().reloadActivities();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.snackBar.warning('Este fornecedor já está presente nesta oferta.');
          } else {
            this.snackBar.error('Erro ao criar fornecedor.');
          }
        }
      });
  }

  private handleDeleteSupplier(data: {
    purchaseId?: string;
    supplierId: string;
    productId: string;
  }): void {
    this.loadingService.showLoading();
    this.purchasingService
      .deleteSupplierProducts(data.supplierId, data.productId)
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.removeSupplierFromList(data.supplierId, data.productId);
          this.snackBar.success('Fornecedor excluído com sucesso!');
          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.snackBar.error('Erro ao excluir fornecedor.');
        }
      });
  }

  private handleUpdateSuggestedSupplierStatus(supplier: PurchaseSupplier, productId: string): void {
    this.loadingService.showLoading();
    this.purchasingService
      .updateSuggestedSupplierStatus({
        purchaseId: this.currentPurchaseId,
        productId,
        supplierId: supplier.id,
        status: PurchaseSupplierLinkStatus.APROVADO
      })
      .pipe(finalize(() => this.loadingService.hideLoading()), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateSupplierLinkStatusInList(supplier.id, productId, PurchaseSupplierLinkStatus.APROVADO);
          this.snackBar.success('Fornecedor aceito com sucesso!');
          this.activityPanel().reloadActivities();
        },
        error: (error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.snackBar.warning('Este fornecedor sugerido já foi avaliado.');
          } else if (error.status === 404) {
            this.snackBar.warning('Fornecedor sugerido não encontrado.');
          } else {
            this.snackBar.error('Erro ao atualizar o status do fornecedor sugerido.');
          }
        }
      });
  }

  private updateSupplierLinkStatusInList(
    supplierId: string,
    productId: string,
    status: PurchaseSupplierLinkStatus
  ): void {
    const nextItems = this.items().map(item => {
      if (item.productId !== productId) return item;
      return {
        ...item,
        suppliers: item.suppliers.map(supplier =>
          supplier.id === supplierId ? { ...supplier, linkStatus: status } : supplier
        )
      };
    });

    this.items.set(nextItems);

    const item = nextItems.find(currentItem => currentItem.productId === productId);
    if (item) {
      this.suppliersDialogRef?.componentInstance.updatedSuppliersList(item.suppliers);
    }
  }

  private handleMergeRequests(requests: MergeItem[]): void {
    this.loadingService.showLoading();
    this.purchasingService
      .mergePurchaseRequestItems({
        purchaseId: this.currentPurchaseId,
        mergingItems: requests.map(request => ({
          orderId: request.orderId,
          originalPurchaseId: request.purchaseId
        }))
      })
      .pipe(
        switchMap(() =>
          forkJoin({
            items: this.purchasingService.findAllProductsForQuote(
              this.currentPurchaseId,
              PurchaseRequestStatus.ACCEPTED
            ),
            duplicatedItems: this.purchasingService.checkForDuplicatedProducts(
              this.currentPurchaseId
            )
          })
        ),
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ items, duplicatedItems }) => {
          this.items.set(items.body || []);
          this.duplicatedItems.set(duplicatedItems.body || []);
          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.snackBar.error('Erro ao unificar solicitações');
        }
      });
  }

  private pushNewCreateSupplier(newSupplier: PurchaseSupplier, productId: string): void {
    const nextItems = this.items().map(item => {
      if (item.productId !== productId) return item;
      return { ...item, suppliers: [...item.suppliers, newSupplier] };
    });

    this.items.set(nextItems);
    this.updateSupplierDialogsAfterCreate(nextItems, productId);
  }

  private removeSupplierFromList(supplierId: string, productId: string): void {
    const nextItems = this.items().map(item => {
      if (item.productId !== productId) return item;
      return {
        ...item,
        suppliers: item.suppliers.filter(supplier => supplier.id !== supplierId)
      };
    });

    this.items.set(nextItems);
    this.updateSupplierDialogsAfterDelete(supplierId, productId);
  }

  private updateSupplierDialogsAfterCreate(
    items: SendPurchaseRequestForQuoteItem[],
    productId: string
  ): void {
    const item = items.find(currentItem => currentItem.productId === productId);
    if (!item) return;

    this.suppliersDialogRef?.componentInstance.updatedSuppliersList(item.suppliers);
  }

  private updateSupplierDialogsAfterDelete(supplierId: string, productId: string): void {
    const item = this.items().find(currentItem => currentItem.productId === productId);
    if (item) {
      this.suppliersDialogRef?.componentInstance.updatedSuppliersList(item.suppliers);
    }

  }

  private startQuoting(): void {
    this.loadingService.showLoading();
    this.purchasingService
      .startPurchaseRequestQuoting(this.currentPurchaseId)
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.snackBar.success('Solicitação enviada para cotação com sucesso!');
          this.router.navigate(['../../quadro'], { relativeTo: this.route });
        },
        error: () => {
          this.snackBar.error('Erro ao atualizar o status da solicitação');
        }
      });
  }

  private showErrorDialog(reloadAction: () => void): void {
    this.confirmationModal
      .open(
        {
          headerTitle: 'Erro ao carregar',
          title: 'Não foi possível carregar os dados',
          description:
            'Ocorreu um problema ao tentar buscar as informações. Deseja tentar novamente?',
          labelConfirm: 'Recarregar',
          labelCancel: 'Entendi'
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (res === Action.CONFIRM) {
          reloadAction();
        } else {
          this.handleGoBack();
        }
      });
  }

  protected handleTableAction(event: PurchaseRequestItemTableActionEvent): void {
    if (event.actionType === 'remove-item') {
      this.removeItem(event.item);
    }
  }

  protected handleQuantityChange(event: {
    item: SendPurchaseRequestForQuoteItem;
    newQuantity: number;
  }): void {
    const { item, newQuantity } = event;

    this.loadingService.showLoading();
    this.purchasingService
      .updatePurchaseItemInfo(item.orderId, { quantity: newQuantity })
      .pipe(
        finalize(() => this.loadingService.hideLoading()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.items.update(currentItems =>
            currentItems.map(i =>
              i.orderId === item.orderId ? { ...i, quantity: newQuantity } : i
            )
          );
          this.snackBar.success('Quantidade alterada com sucesso!');
          this.activityPanel().reloadActivities();
        },
        error: () => {
          this.snackBar.error('Erro ao alterar a quantidade do item.');
        }
      });
  }

  private removeItem(item: SendPurchaseRequestForQuoteItem): void {
    this.confirmationModal
      .openWithJustification(
        {
          headerTitle: 'Remover produto',
          title: 'Motivo da remoção',
          description: `Digite o motivo da remoção do produto ${item.name}.`,
          labelConfirm: 'Remover produto',
          labelCancel: 'Cancelar'
        },
        {
          justificationPlaceholder: 'Digite aqui o motivo pelo qual o item foi removido.',
          justificationRequired: true
        },
        ModalTypes.DANGER
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: ConfirmResult) => {
        if (result.action !== Action.CONFIRM || !result.justification) return;

        const payload: UpdatePurchaseOrderItemDto[] = [
          {
            orderId: item.orderId,
            status: PurchaseItemStatus.REMOVED,
            reason: result.justification
          }
        ];

        this.loadingService.showLoading();
        this.purchasingService
          .updatePurchaseOrdersStatus(this.currentPurchaseId, payload)
          .pipe(
            finalize(() => this.loadingService.hideLoading()),
            takeUntilDestroyed(this.destroyRef)
          )
          .subscribe({
            next: () => {
              this.items.update(items => items.filter(i => i.orderId !== item.orderId));
              this.snackBar.success('Produto removido com sucesso!');
              this.activityPanel().reloadActivities();
            },
            error: () => {
              this.snackBar.error('Erro ao remover o produto.');
            }
          });
      });
  }

  protected openActivityTab(): void {
    this.activityPanel().openTab(0);
  }

  protected openCommentsTab(): void {
    this.activityPanel().openTab(1);
  }
}
